import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { adminAuth } from '../../helpers/api/adminApi';

interface AdminSidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ collapsed, onToggle }) => {
    const location = useLocation();
    const admin = adminAuth.getUser();

    const isActive = (path: string) => {
        if (path === '/admin/dashboard') {
            return location.pathname === '/admin/dashboard' || location.pathname === '/admin';
        }
        return location.pathname.startsWith(path);
    };

    const navSections = [
        {
            title: null,
            items: [
                { path: '/admin/dashboard', label: 'Dashboard', icon: 'grid' },
            ]
        },
        {
            title: 'CUSTOMERS',
            items: [
                { path: '/admin/customers/users', label: 'Users', icon: 'users' },
                { path: '/admin/customers/workspaces', label: 'Workspaces', icon: 'briefcase' },
            ]
        },
        {
            title: 'SUBSCRIPTIONS',
            items: [
                { path: '/admin/subscriptions', label: 'Subscription Plans', icon: 'credit-card', exact: true },
                { path: '/admin/subscriptions/usage', label: 'Credits & Usage', icon: 'pie-chart' },
            ]
        },
        {
            title: 'WHATSAPP',
            items: [
                { path: '/admin/whatsapp/accounts', label: 'WhatsApp Accounts', icon: 'message-circle' },
                { path: '/admin/whatsapp/message-logs', label: 'Message Logs', icon: 'file-text' },
            ]
        },
        {
            title: 'AI',
            items: [
                { path: '/admin/ai/usage', label: 'AI Usage', icon: 'cpu' },
                { path: '/admin/ai/providers', label: 'AI Provider Status', icon: 'activity' },
            ]
        },
        {
            title: 'MCP',
            items: [
                { path: '/admin/mcp/servers', label: 'Servers', icon: 'server' },
                { path: '/admin/mcp/tools', label: 'Tools', icon: 'tool' },
                { path: '/admin/mcp/apps', label: 'User Apps', icon: 'grid' },
                { path: '/admin/mcp/executions', label: 'Executions', icon: 'play' },
            ]
        },
        {
            title: 'AUTOMATION',
            items: [
                { path: '/admin/automation/list', label: 'Automations', icon: 'zap' },
                { path: '/admin/automation/executions', label: 'Executions', icon: 'clock' },
            ]
        },
        {
            title: 'SETTINGS',
            items: [
                { path: '/admin/settings/roles', label: 'Roles', icon: 'shield' },
                { path: '/admin/settings/permissions', label: 'Permissions', icon: 'lock' },
                { path: '/admin/settings/audit-logs', label: 'Audit Logs', icon: 'list' },
                { path: '/admin/settings/system', label: 'System Settings', icon: 'settings' },
            ]
        },
        {
            title: 'PROFILE',
            items: [
                { path: '/admin/profile', label: 'My Profile', icon: 'user' },
            ]
        }
    ];

    const handleLogout = () => {
        adminAuth.clearSession();
        window.location.href = '/admin/login';
    };

    return (
        <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
            <div className="admin-sidebar-header">
                <Link to="/admin/dashboard" className="admin-brand">
                    <div className="admin-brand-icon">
                        <FeatherIcon icon="shield" size="20" />
                    </div>
                    {!collapsed && (
                        <div className="admin-brand-text">
                            <span className="brand-title">SKYNYX</span>
                            <span className="brand-badge">SUPER ADMIN</span>
                        </div>
                    )}
                </Link>
                <button className="btn btn-sm text-white admin-toggle-btn d-md-none" onClick={onToggle}>
                    <FeatherIcon icon="x" size="18" />
                </button>
            </div>

            <div className="admin-sidebar-scroll">
                <nav className="admin-nav">
                    {navSections.map((section, idx) => (
                        <div key={idx} className="admin-nav-group">
                            {section.title && !collapsed && (
                                <div className="admin-nav-title">{section.title}</div>
                            )}
                            <ul className="admin-nav-list">
                                {section.items.map((item, itemIdx) => {
                                    const active = item.exact 
                                        ? location.pathname === item.path 
                                        : isActive(item.path);
                                    return (
                                        <li key={itemIdx} className={`admin-nav-item ${active ? 'active' : ''}`}>
                                            <Link to={item.path} className="admin-nav-link" title={collapsed ? item.label : undefined}>
                                                <FeatherIcon icon={item.icon} size="18" className="admin-nav-icon" />
                                                {!collapsed && <span className="admin-nav-text">{item.label}</span>}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}

                    <div className="admin-nav-group mt-3 pt-2 border-top border-secondary">
                        <ul className="admin-nav-list">
                            <li className="admin-nav-item">
                                <button onClick={handleLogout} className="admin-nav-link text-danger border-0 bg-transparent w-100 text-start">
                                    <FeatherIcon icon="log-out" size="18" className="admin-nav-icon" />
                                    {!collapsed && <span className="admin-nav-text">Logout</span>}
                                </button>
                            </li>
                        </ul>
                    </div>
                </nav>
            </div>

            {!collapsed && admin && (
                <div className="admin-sidebar-footer">
                    <div className="admin-user-avatar">
                        {admin.name ? admin.name.charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div className="admin-user-meta">
                        <div className="admin-user-name text-truncate">{admin.name || 'Administrator'}</div>
                        <div className="admin-user-role">{admin.role || 'Super Admin'}</div>
                    </div>
                </div>
            )}
        </aside>
    );
};

export default AdminSidebar;
